import { parseFullName } from 'parse-full-name';
import { hasFeatureInLocalStorage, SMS_TEMPLATE_VARIABLES_FEATURE } from '../global/featureFlags';

// Supported template tokens, whitespace-tolerant inside the braces
// (e.g. `{{ first_name }}` works). Unknown tokens are left untouched.
const FIRST_NAME_TOKEN_SOURCE = String.raw`\{\{\s*first_name\s*\}\}`;
const FIRST_NAME_TOKEN = new RegExp(FIRST_NAME_TOKEN_SOURCE, 'gi');
const REFERRING_PROVIDER_TOKEN = /\{\{\s*referring_provider\s*\}\}/gi;
const BLANK_TOKEN_MARKER = '\uE000';

// Non-global copy so `.test()` never leaves lastIndex state between calls.
const FIRST_NAME_TOKEN_TEST = new RegExp(FIRST_NAME_TOKEN_SOURCE, 'i');
const hasFirstNameToken = (text) => FIRST_NAME_TOKEN_TEST.test(text || '');
const normalizeSupportedTokenValue = (value) => (
  typeof value === 'string' && value.trim() ? value : ''
);

// Remove only spacing artifacts adjacent to a token substituted with a blank
// value. Authored spacing elsewhere in the template is preserved.
const tidyBlankSubstitutions = (text) => text
  .replace(/\uE000(?:[ \t]*\uE000)+/g, BLANK_TOKEN_MARKER)
  .replace(/^[ \t]*\uE000[ \t]*[,.!?;:][ \t]*/, '')
  .replace(/[ \t]*\uE000[ \t]*([,.!?;:])/g, '$1')
  .replace(/^[ \t]*\uE000[ \t]*/, '')
  .replace(/[ \t]*\uE000[ \t]*$/, '')
  .replace(/[ \t]+\uE000[ \t]+/g, ' ')
  .replace(/\uE000/g, '');

// First word of the patient name, matching the legacy `parseFullName(...)?.first`
// behavior used everywhere the send-SMS modal is opened.
export const getPatientFirstName = (patientName) => (
  parseFullName(patientName)?.first || patientName || ''
);

/**
 * Builds the SMS message text that lands in the editable text area when a
 * template is selected (either the default template on modal open, or a
 * template picked from the AutoComplete dropdown).
 *
 * Flag OFF (sms_template_variables): legacy behavior — the raw template message
 * is always prefixed with "Hello[ FirstName], " (byte-identical to the previous
 * inline logic).
 *
 * Flag ON: substitutes {{first_name}} (first word of the patient name) and
 * {{referring_provider}} (whitespace-tolerant, blank values -> empty string,
 * unknown tokens untouched). If the template already contains a {{first_name}}
 * token the auto "Hello X," greeting is suppressed; otherwise the legacy
 * greeting prefix is kept.
 *
 * @param {Object} params
 * @param {string} params.templateMessage - raw SmsTemplate message body
 * @param {string} [params.patientName]
 * @param {string} [params.referringProvider]
 * @returns {string}
 */
export const buildSmsTemplateMessage = ({
  templateMessage = '',
  patientName,
  referringProvider,
} = {}) => {
  const firstName = getPatientFirstName(patientName);

  if (!hasFeatureInLocalStorage(SMS_TEMPLATE_VARIABLES_FEATURE)) {
    return `Hello${firstName ? ` ${firstName}` : ''}, ${templateMessage}`;
  }

  const normalizedFirstName = normalizeSupportedTokenValue(firstName);
  const normalizedReferringProvider = normalizeSupportedTokenValue(referringProvider);
  const substituted = tidyBlankSubstitutions(
    (templateMessage || '')
      .replace(FIRST_NAME_TOKEN, normalizedFirstName || BLANK_TOKEN_MARKER)
      .replace(REFERRING_PROVIDER_TOKEN, normalizedReferringProvider || BLANK_TOKEN_MARKER),
  );

  // Template greets the patient itself, so suppress the auto greeting.
  if (hasFirstNameToken(templateMessage)) {
    return substituted;
  }

  const greeting = `Hello${normalizedFirstName ? ` ${normalizedFirstName}` : ''},`;
  return substituted ? `${greeting} ${substituted}` : greeting;
};

const getSmsTemplateIdentity = (template) => (
  template.id
  ?? template.slug
  ?? `${template.owner_type}-${template.owner_id}-${template.message}`
);

export const buildSmsTemplateOption = ({
  template,
}) => {
  return {
    value: `sms-template-${getSmsTemplateIdentity(template)}`,
    label: template.message,
  };
};

export const buildSmsTemplateSelectionState = (
  selectedValue,
  templates,
  { patientName, referringProvider } = {},
) => {
  const selectedTemplate = templates.find(
    (template) => `sms-template-${getSmsTemplateIdentity(template)}` === selectedValue,
  );
  if (!selectedTemplate) {
    return { messageText: selectedValue };
  }

  return {
    messageText: buildSmsTemplateMessage({
      templateMessage: selectedTemplate.message,
      patientName,
      referringProvider,
    }),
    originalUrl: selectedTemplate.url,
    url: selectedTemplate.short_url,
    shortenedUrl: null,
  };
};

export const isSmsTemplateSelectionValue = (value, options) => (
  options.some((option) => option.value === value)
);
